import React, { type CSSProperties } from 'react';

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const h = hex.replace(/^#/, '').trim();
  if (h.length === 3) {
    return {
      r: parseInt(h[0]! + h[0]!, 16),
      g: parseInt(h[1]! + h[1]!, 16),
      b: parseInt(h[2]! + h[2]!, 16),
    };
  }
  if (h.length === 6) {
    const n = parseInt(h, 16);
    if (Number.isNaN(n)) return null;
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
  }
  return null;
}

const rgba = (r: number, g: number, b: number, a: number) => `rgba(${r},${g},${b},${a})`;

function auraMeshBarStyle(accentHex: string, isDark: boolean, salt: number): CSSProperties {
  const fallback = isDark ? { r: 96, g: 165, b: 250 } : { r: 59, g: 130, b: 246 };
  const rgb = hexToRgb(accentHex) ?? fallback;
  const { r, g, b } = rgb;
  const rHi = Math.min(255, r + 42);
  const gHi = Math.min(255, g + 28);
  const bHi = Math.max(0, b - 18);
  const rLo = Math.max(0, r - 32);
  const gLo = Math.min(255, g + 36);
  const bLo = Math.min(255, b + 52);
  const boost = isDark ? 1.08 : 1;
  const ox = ((salt * 17) % 24) - 12;
  const oy = ((salt * 23) % 20) - 10;
  const mesh = [
    `radial-gradient(ellipse 95% 90% at ${18 + ox * 0.4}% ${32 + oy}%, ${rgba(rHi, gHi, bHi, 0.48 * boost)} 0%, transparent 58%)`,
    `radial-gradient(ellipse 80% 95% at ${84 - ox * 0.3}% ${22 - oy * 0.5}%, ${rgba(r, g, b, 0.44 * boost)} 0%, transparent 52%)`,
    `radial-gradient(ellipse 85% 75% at ${48 + ox}% ${92 + oy * 0.4}%, ${rgba(rLo, gLo, bLo, 0.42 * boost)} 0%, transparent 55%)`,
    `linear-gradient(128deg, ${rgba(r, g, b, isDark ? 0.88 : 0.92)} 0%, ${rgba(rHi, gHi, bHi, 0.84)} 38%, ${rgba(rLo, gLo, bLo, 0.88)} 100%)`,
  ].join(', ');
  return {
    backgroundImage: mesh,
    backgroundColor: accentHex,
    boxShadow: `0 0 14px -2px ${rgba(r, g, b, isDark ? 0.35 : 0.28)}, 0 0 6px -2px ${rgba(rHi, gHi, bHi, 0.25)}`,
  };
}

export function fmtWatts(n: number): string {
  const v = Math.round(n * 10) / 10;
  return `${v} W`;
}

export function fmtHours(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return `${n} h`;
}

interface UsageBarProps {
  title: string;
  used: number;
  total: number;
  usedLabel: string;
  restLabel: string;
  accent: string;
  isDark: boolean;
  emptyText?: string;
  /** Shown after the total watts, e.g. "total" or "Max". */
  totalSuffix?: string;
}

const UsageBar: React.FC<UsageBarProps> = ({
  title,
  used,
  total,
  usedLabel,
  restLabel,
  accent,
  isDark,
  emptyText,
  totalSuffix = 'total',
}) => {
  const safeTotal = total > 0 ? total : 0;
  const ratio = safeTotal > 0 ? Math.min(1, Math.max(0, used / safeTotal)) : 0;
  const over = safeTotal > 0 && used > safeTotal;
  const fill = over ? '#f87171' : accent;
  const salt = title.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);

  return (
    <div>
      <div className={`flex items-center justify-between text-sm mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
        <span className="font-medium truncate mr-2">{title}</span>
        <span className={`flex-shrink-0 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
          {safeTotal > 0 ? `${fmtWatts(safeTotal)} ${totalSuffix}` : '—'}
        </span>
      </div>
      <div
        className={`w-full h-2 rounded-full overflow-visible ${
          isDark ? 'bg-[#2b3140]/90 ring-1 ring-white/[0.06]' : 'bg-gray-100 ring-1 ring-black/[0.04]'
        }`}
      >
        <div
          className="h-full min-w-0 rounded-full transition-[width] duration-500 ease-out"
          style={{
            width: `${ratio * 100}%`,
            ...(ratio > 0 ? auraMeshBarStyle(fill, isDark, salt) : { backgroundColor: 'transparent' }),
          }}
        />
      </div>
      <div className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
        {safeTotal <= 0 && emptyText
          ? emptyText
          : `${usedLabel}: ${fmtWatts(used)} · ${restLabel}: ${fmtWatts(Math.max(0, safeTotal - used))}`}
      </div>
    </div>
  );
};

export default UsageBar;
